import { defineStore } from "pinia";
import { isMiniProgramWebview } from "@/utils/miniprogram-bridge";
import { syncNativeSystemBarsTheme } from "@/utils/native-status-bar";

let mediaQuery = null;
let isListening = false;
const DEFAULT_THEME = "light";
const THEME_STORAGE_KEY = "__theme";

function getStoredTheme() {
    if (typeof window === "undefined") {
        return null;
    }

    const theme = String(window.localStorage?.getItem(THEME_STORAGE_KEY) || "").trim();
    if (theme === "light" || theme === "dark") {
        return theme;
    }

    return null;
}

function getSystemTheme() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return DEFAULT_THEME;
    }

    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export const useThemeStore = defineStore("theme", {
    state: () => ({
        isDark: DEFAULT_THEME === "dark",
        theme: DEFAULT_THEME,
        userTheme: null,
    }),
    getters: {
        isSystemTheme(state) {
            return !state.userTheme;
        },
    },
    actions: {
        init() {
            if (typeof window === "undefined") {
                return;
            }

            this.userTheme = getStoredTheme();

            if (typeof window.matchMedia === "function") {
                mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
            } else {
                mediaQuery = null;
            }

            this.syncResolvedTheme();

            if (isListening) {
                return;
            }

            const syncTheme = () => {
                this.userTheme = getStoredTheme();
                this.syncResolvedTheme();
            };

            if (mediaQuery) {
                if (typeof mediaQuery.addEventListener === "function") {
                    mediaQuery.addEventListener("change", syncTheme);
                } else if (typeof mediaQuery.addListener === "function") {
                    mediaQuery.addListener(syncTheme);
                }
            }

            window.addEventListener("storage", (event) => {
                if (event.key !== THEME_STORAGE_KEY) {
                    return;
                }
                syncTheme();
            });

            isListening = true;
        },
        syncResolvedTheme() {
            const resolvedTheme = isMiniProgramWebview()
                ? "light"
                : this.userTheme || getSystemTheme() || DEFAULT_THEME;
            this.setTheme(resolvedTheme === "dark");
        },
        setTheme(isDark) {
            this.isDark = !!isDark;
            this.theme = this.isDark ? "dark" : "light";

            if (typeof document !== "undefined") {
                const root = document.documentElement;
                const rootClassList = root.classList;
                rootClassList.remove("light", "dark");
                rootClassList.add(this.theme);
                rootClassList.toggle("ion-palette-dark", this.isDark);
                root.setAttribute("data-theme", this.theme);
                root.style.background = this.isDark ? "#0b1320" : "#f6f7f9";
                root.style.colorScheme = this.isDark ? "dark" : "light";

                if (document.body?.classList) {
                    document.body.classList.toggle("ion-palette-dark", this.isDark);
                    document.body.style.background = this.isDark ? "#0b1320" : "#f6f7f9";
                }
            }

            syncNativeSystemBarsTheme(this.isDark);
        },
        setUserTheme(theme) {
            if (typeof window === "undefined") {
                return;
            }

            if (theme === "light" || theme === "dark") {
                window.localStorage?.setItem(THEME_STORAGE_KEY, theme);
            } else {
                window.localStorage?.removeItem(THEME_STORAGE_KEY);
            }

            this.userTheme = getStoredTheme();
            this.syncResolvedTheme();
        },
    },
});

export function initStore(pinia) {
    useThemeStore(pinia).init();
}
